import { createFormHook, createFormHookContexts, useStore } from "@tanstack/react-form";
import type { ComponentProps } from "react";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@corpuscomplete/web-ui/components/input-group";
import {
  Input as InputComponent,
  type InputProps as WebInputProps,
} from "@corpuscomplete/web-ui/components/input";
import {
  Textarea as TextareaComponent,
  type TextareaProps as WebTextareaProps,
} from "@corpuscomplete/web-ui/components/textarea";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { Label } from "@corpuscomplete/web-ui/components/label";
import { cn } from "@corpuscomplete/web-ui/lib/utils";
import { AppForm } from "@corpuscomplete/core-ui/form";
import {
  RadioGroup as RadioGroupComponent,
  RadioGroupItem,
} from "@corpuscomplete/web-ui/components/radio-group";
import { SelectionCard } from "@corpuscomplete/web-ui/components/selection-card";

type AppInputProps = Omit<WebInputProps, "className"> & {
  label?: string;
  className?: string;
  inputClassName?: string;
  startText?: string;
  endText?: string;
};

type AppTextareaProps = Omit<WebTextareaProps, "className"> & {
  label?: string;
  className?: string;
  textareaClassName?: string;
};

type AppRadioGroupOption<T extends string> = {
  label: string;
  value: T;
  description?: string;
};

type AppRadioGroupProps<T extends string> = Omit<
  ComponentProps<typeof RadioGroupComponent>,
  "value" | "onValueChange" | "className"
> & {
  label?: string;
  className?: string;
  options: AppRadioGroupOption<T>[] | readonly AppRadioGroupOption<T>[];
  onBeforeChange?: (value: T) => void;
  onAfterChange?: (value: T) => void;
};

export const { fieldContext, formContext, useFieldContext, useFormContext } =
  createFormHookContexts();

function LabelWrapper({
  label,
  errors = [],
  children,
  className,
}: {
  label?: string;
  children: React.ReactNode;
  errors?: AppForm.FormErrors;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {label && <Label className="text-xs leading-none">{label}</Label>}
      {children}
      <FormErrors errors={errors} />
    </div>
  );
}

function Input({
  label,
  className,
  inputClassName,
  startText,
  endText,
  variant,
  textVariant,
  ...props
}: AppInputProps) {
  const field = useFieldContext<string>();
  const errors = useStore(field.store, (state) => state.meta.errors);

  if (variant === "ghost" && !startText && !endText) {
    return (
      <LabelWrapper label={label} errors={errors} className={className}>
        <InputComponent
          {...props}
          disabled={props.disabled}
          value={field.state.value ?? ""}
          variant={variant}
          textVariant={textVariant}
          className={inputClassName}
          onChange={(e) => field.handleChange(e.target.value)}
        />
      </LabelWrapper>
    );
  }

  return (
    <LabelWrapper label={label} errors={errors} className={className}>
      <InputGroup>
        {startText && (
          <InputGroupAddon align="inline-start">
            <InputGroupText>{startText}</InputGroupText>
          </InputGroupAddon>
        )}
        <InputGroupInput
          {...props}
          disabled={props.disabled}
          value={field.state.value ?? ""}
          className={inputClassName}
          onChange={(e) => field.handleChange(e.target.value)}
        />
        {endText && (
          <InputGroupAddon align="inline-end">
            <InputGroupText>{endText}</InputGroupText>
          </InputGroupAddon>
        )}
      </InputGroup>
    </LabelWrapper>
  );
}

function Textarea({
  label,
  placeholder,
  className,
  textareaClassName,
  autoFocus,
  variant,
  textVariant,
}: AppTextareaProps) {
  const field = useFieldContext<string>();
  const errors = useStore(field.store, (state) => state.meta.errors);
  return (
    <LabelWrapper label={label} errors={errors} className={className}>
      <TextareaComponent
        placeholder={placeholder}
        autoFocus={autoFocus}
        value={field.state.value ?? ""}
        variant={variant}
        textVariant={textVariant}
        className={textareaClassName}
        onChange={(e) => field.handleChange(e.target.value)}
      />
    </LabelWrapper>
  );
}

function RadioGroup<T extends string>({
  label,
  className,
  options,
  onBeforeChange,
  onAfterChange,
}: AppRadioGroupProps<T>) {
  const field = useFieldContext<string>();
  const errors = useStore(field.store, (state) => state.meta.errors);
  const hasDescriptions = options.some((option) => option.description);

  function handleChange(value: T) {
    onBeforeChange?.(value);
    field.handleChange(value);
    onAfterChange?.(value);
  }

  if (hasDescriptions) {
    return (
      <LabelWrapper label={label} errors={errors} className={className}>
        <RadioGroupComponent
          value={field.state.value ?? ""}
          onValueChange={(value) => handleChange(value as T)}
          className="grid grid-cols-1 gap-3 sm:grid-cols-2"
        >
          {options.map((option) => (
            <SelectionCard
              key={option.value}
              type="radio"
              selected={field.state.value === option.value}
              onSelect={() => handleChange(option.value)}
              title={option.label}
              description={option.description ?? ""}
              value={option.value}
            />
          ))}
        </RadioGroupComponent>
      </LabelWrapper>
    );
  }

  return (
    <LabelWrapper label={label} errors={errors} className={className}>
      <RadioGroupComponent
        value={field.state.value ?? ""}
        onValueChange={(value) => handleChange(value as T)}
      >
        {options.map((option) => (
          <div key={option.value} className="flex items-center gap-2">
            <RadioGroupItem id={option.value} key={option.value} value={option.value} />
            <Label htmlFor={option.value} className="text-xs leading-none">
              {option.label}
            </Label>
          </div>
        ))}
      </RadioGroupComponent>
    </LabelWrapper>
  );
}

export const formHooks = createFormHook({
  fieldContext,
  formContext,
  fieldComponents: {
    Input,
    Textarea,
    RadioGroup,
  },
  formComponents: {},
});

AppForm.registerHooks(formHooks);

type HookBundle = typeof formHooks;

declare module "@corpuscomplete/core-ui/form" {
  export namespace AppForm {
    // eslint-disable-next-line no-unused-vars -- module augmentation registry consumed through type lookup
    interface TypeRegistry {
      hooks: HookBundle;
    }
  }
}

export const { useAppForm, withFieldGroup, withForm } = formHooks;

export function FormErrors({ errors = [] }: { errors?: AppForm.FormErrors }) {
  return (
    <>
      {errors.map(
        (error, i) =>
          error && (
            <Text key={i} variant="xs" className="text-destructive">
              {error.message}
            </Text>
          ),
      )}
    </>
  );
}
